import { useCallback, useState } from "react";
import { Button, buttonVariants, cn } from "@tellescope/ui";
import type { ButtonBlock as ButtonBlockType } from "../../../shared/schema";
import { hasAsyncPlaceholders } from "../../../shared/template";
import { useAsyncHrefNavigation } from "../../hooks/useAsyncHrefNavigation";

interface ButtonBlockProps {
  text: ButtonBlockType["props"]["text"];
  variant: ButtonBlockType["props"]["variant"];
  href?: ButtonBlockType["props"]["href"];
}

export const ButtonBlock: React.FC<ButtonBlockProps> = ({ text, variant = "default", href }) => {
  const openAsyncHref = useAsyncHrefNavigation();
  const [isResolving, setIsResolving] = useState(false);

  const handleClick = useCallback(async () => {
    if (!href) return;

    setIsResolving(true);
    try {
      await openAsyncHref(href);
    } finally {
      setIsResolving(false);
    }
  }, [href, openAsyncHref]);

  if (!text) return null;

  // Rendered as a button, not an anchor: the URL doesn't exist until the click resolves it,
  // so an href here would be one that only works via this handler — Cmd-click, middle-click,
  // and "copy link address" would all hand back the raw {{...}} placeholder.
  if (href && hasAsyncPlaceholders(href)) {
    return (
      <Button variant={variant} loading={isResolving} onClick={handleClick}>
        {text}
      </Button>
    );
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={cn(buttonVariants({ variant }))}>
        {text}
      </a>
    );
  }

  return <Button variant={variant}>{text}</Button>;
};
